import { useState, type Dispatch, type ReactNode, type SetStateAction } from 'react'
import { Modal } from '@systemeio/components'
import { Menu01Icon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { LocaleEnum } from 'shared/enums/locale-enum'
import useUser from 'shared/hooks/use-user'
import { useGetLoginUrl } from 'shared/utils/url-utils'
import { PUBLIC_LAYOUT_X_PADDING } from '../../constants/public-layout-constants'
import PublicLanguageSwitcher from '../../public-language-switcher'
import { topMenuItems } from '../constants/top-menu-items'
import PublicHeaderLoginIcon from '../icons/public-header-login-icon'
import NavLink from './nav-link'

const MobileMenu = (): ReactNode => {
  const [isOpen, setIsOpen] = useState(false)
  const { user } = useUser()
  const { t } = useTranslation()
  const router = useRouter()
  const locale = router.locale as LocaleEnum
  const loginUrl = useGetLoginUrl()

  return (
    <>
      <BurgerButton isOpen={isOpen} setIsOpen={setIsOpen} />
      <Modal.Root open={isOpen} onOpenChange={setIsOpen}>
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Viewport className="place-items-start justify-items-end p-0">
            <Modal.Popup
              showCloseButton={false}
              className="w-auto max-w-none rounded-none border-0 bg-bg-primary"
            >
              <div
                className={`flex min-w-68 flex-col gap-5 overflow-auto pt-[92px] pb-10 font-inter text-text-primary ${PUBLIC_LAYOUT_X_PADDING}`}
              >
                <BurgerButton
                  className="absolute top-4 right-4"
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
                />
                {topMenuItems
                  .filter(item => !item.excludeLocales?.includes(locale))
                  .map((item, index) => (
                    <NavLink key={index} href={item.href}>
                      <div className="flex items-center gap-1.5">
                        <div className="flex min-w-[33px] justify-center">{item.icon}</div>
                        <span>{t(item.title)}</span>
                      </div>
                    </NavLink>
                  ))}
                <NavLink signInLink noLoco href={user ? '/dashboard' : loginUrl}>
                  <div className="flex items-center gap-1.5">
                    <div className="flex min-w-[33px] justify-center">
                      <PublicHeaderLoginIcon />
                    </div>
                    {user ? t('global.dashboard') : t('public.public_header.sing_in')}
                  </div>
                </NavLink>
                <PublicLanguageSwitcher mobile />
              </div>
            </Modal.Popup>
          </Modal.Viewport>
        </Modal.Portal>
      </Modal.Root>
    </>
  )
}

const BurgerButton = ({
  isOpen,
  setIsOpen,
  className,
}: {
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
  className?: string
}): ReactNode => (
  <button
    type="button"
    aria-expanded={isOpen}
    aria-label={isOpen ? 'Close menu' : 'Open menu'}
    className={`${className || ''} group flex size-10 items-center justify-center rounded-md outline-hidden focus-visible:focus-ring-primary lg:hidden`}
    onClick={() => setIsOpen(!isOpen)}
  >
    <Menu01Icon className="size-6 text-text-primary" aria-hidden="true" />
  </button>
)

export default MobileMenu
